import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchProjects,
  createProject,
  updateProject,
  deleteProject,
} from '../../features/Projects/projectSlice';
import { fetchLanguages } from '../../features/Languages/languageSlice';
import { fetchServices } from '../../features/Services/serviceSlice';
import { fetchClients, fetchAcceptedClients  } from '../../features/Users/userSlice';
import {
  Button,
  Table,
  Modal,
  Form,
  Input,
  Space,
  Popconfirm,
  message,
  Select,
  DatePicker,
  Spin,
} from 'antd';
import { EditOutlined, DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../api/api';
import AssignTranslatorsModal from '../../features/Projects/components/AssignTranslatorsModal';
import BidModal from '../../features/Projects/components/BidModal';
import { useNavigate } from 'react-router-dom';

const Projects = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Fetch data from Redux store
  const { projects = [], loading: projectsLoading, pagination } = useSelector((state) => state.projects);
  const { acceptedClients, loading: usersLoading } = useSelector((state) => state.users);
  const users = acceptedClients ; // Use acceptedClients instead of clients.data
  const { languages = [], loading: languagesLoading } = useSelector((state) => state.languages);
  const { services = [], loading: servicesLoading } = useSelector((state) => state.services);

  // Local state for search and filters
  const [filters, setFilters] = useState({
    search: '',
    source_language_id: null,
    target_language_ids: [],
    service_id: null,
    page: 1,
    pageSize: 10,
  });

  const [bidModalProject, setBidModalProject] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingProject, setEditingProject] = useState(null);
  const [downloadingFileId, setDownloadingFileId] = useState(null);
  const [assignModalProject, setAssignModalProject] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  // Memoize the assignModalProject to avoid unnecessary re-renders
  const assignModalProjectMemoized = useMemo(() => {
    return assignModalProject;
  }, [assignModalProject?.id]); // Only recreate if the project ID changes

  // Fetch data on component mount and when filters change
  useEffect(() => {
    dispatch(fetchLanguages());
    dispatch(fetchServices());
    dispatch(fetchAcceptedClients()); // Fetch accepted clients instead of all clients
    dispatch(fetchProjects(filters));
  }, [dispatch, filters]);

  // Handle file download
  const handleDownload = async (project) => {
    setDownloadingFileId(project.id);
    try {
      const response = await api.get(`/projects/${project.id}/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', project.file.split('/').pop());
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('File downloaded successfully');
    } catch (error) {
      message.error('Failed to download file');
      console.error(error);
    } finally {
      setDownloadingFileId(null);
    }
  };

  // Handle search input change
  const handleSearch = (value) => {
    setFilters((prev) => ({
      ...prev,
      search: value,
      page: 1, // Reset to the first page when searching
    }));
  };

  // Handle source language filter change
  const handleSourceLanguageChange = (value) => {
    setFilters((prev) => ({
      ...prev,
      source_language_id: value,
      page: 1, // Reset to the first page when filtering
    }));
  };

  // Handle target languages filter change
  const handleTargetLanguagesChange = (value) => {
    setFilters((prev) => ({
      ...prev,
      target_language_ids: value,
      page: 1, // Reset to the first page when filtering
    }));
  };

  // Handle service filter change
  const handleServiceChange = (value) => {
    setFilters((prev) => ({
      ...prev,
      service_id: value,
      page: 1, // Reset to the first page when filtering
    }));
  };

  // Handle table pagination and page size change
  const handleTableChange = (paginationInfo) => {
    setFilters((prev) => ({
      ...prev,
      page: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    }));
  };

  // Handle add project
  const handleAdd = () => {
    setEditingProject(null);
    form.resetFields();
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setIsModalVisible(true);
  };

  // Handle edit project
  const handleEdit = (project) => {
    setEditingProject(project);
    form.setFieldsValue({
      ...project,
      source_language_id: project.source_language.id,
      user_id: {
        value: project.user.id, // The ID
        label: project.user.name, // The name to display
      },
      target_languages: project.target_languages.map((lang) => lang.id),
      services: project.services.map((service) => service.id),
      expected_date: project.expected_date ? dayjs(project.expected_date) : null,
    });
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setIsModalVisible(true);
  };

  // Handle delete project
  const handleDelete = (id) => {
    dispatch(deleteProject(id))
      .unwrap()
      .then(() => message.success('Project deleted successfully'))
      .catch(() => message.error('Failed to delete project'));
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const formData = new FormData();

      // Extract the user_id value if it's an object
      if (values.user_id && typeof values.user_id === 'object') {
        values.user_id = values.user_id.value;
      }

      // Append fields from the form
      Object.entries(values).forEach(([key, value]) => {
        if (key === 'expected_date') {
          if (value) {
            formData.append(key, value.format('YYYY-MM-DD'));
          }
        } else if (Array.isArray(value)) {
          value.forEach((item) => formData.append(`${key}[]`, item));
        } else if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      });

      // Append the file if one was selected
      if (selectedFile) {
        formData.append('file', selectedFile);
      }

      if (editingProject) {
        formData.append('_method', 'PATCH'); // Laravel compatibility
        await dispatch(updateProject({ id: editingProject.id, formData })).unwrap();
        message.success('Project updated successfully');
      } else {
        await dispatch(createProject(formData)).unwrap();
        message.success('Project created successfully');
      }

      setIsModalVisible(false);
    } catch (error) {
      console.error('Error:', error);
      if (error.payload?.errors) {
        const fieldErrors = Object.keys(error.payload.errors).reduce((acc, field) => {
          const fieldPath = field.split('.');
          acc[fieldPath] = error.payload.errors[field];
          return acc;
        }, {});
        form.setFields(
          Object.entries(fieldErrors).map(([field, errors]) => ({
            name: Array.isArray(field) ? field : field.split('.'),
            errors: Array.isArray(errors) ? errors : [errors],
          }))
        );
        if (error.payload.message) {
          message.error(error.payload.message);
        }
      } else {
        message.error(error.message || 'An error occurred');
      }
    }
  };

  // Table columns
  const columns = [
    {
      title: 'ID',
      dataIndex: 'unique_id',
      align: 'center',
      key: 'unique_id',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      align: 'center',
      key: 'name',
    },
    {
      title: 'Bidding',
      align: 'center',
      key: 'bidding',
      render: (_, record) => (
        <Space style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Button onClick={() => setBidModalProject(record)}>
            Send Bids
          </Button>
          <Button onClick={() => navigate(`/projects/${record.id}/manage-bids`)}>View Bids</Button>
        </Space>
      )
    },
    {
      title: 'Quotation',
      align: 'center',
      key: 'quotation',
      render: (_, record) => (
        <Space style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Button onClick={() => navigate(`/projects/${record.id}/quotations`)}>
            Manage Quotations
          </Button>
        </Space>
      )
    },
    {
      title: 'Client',
      dataIndex: ['user', 'name'],
      align: 'center',
      key: 'user',
    },
    {
      title: 'Source Language',
      dataIndex: ['source_language', 'name'],
      align: 'center',
      key: 'source_language',
    },
    {
      title: 'Assigned Translator',
      align: 'center',
      key: 'Assigned Translator',
      render: (_, record) => {
        const hasQuotations = record.quotations && record.quotations.length > 0;
        if (!hasQuotations) {
          return <span>No Quotation Yet</span>;
        }
        const latestQuotation = record.quotations[record.quotations.length - 1];
        const quotationStatus = latestQuotation.status;
        if (quotationStatus === 'pending') {
          return <span>Quotation Still Pending</span>;
        } else if (quotationStatus === 'rejected') {
          return <span>Quotation Rejected</span>;
        } else if (quotationStatus === 'accepted') {
          return (
            <Button
              type="link"
              onClick={() => navigate(`/projects/${record.id}/assignments`)}
            >
              Manage Assignments
            </Button>
          );
        }
        return <span>No Quotation Yet</span>;
      },
    },
    {
      title: 'File',
      align: 'center',
      key: 'file',
      render: (_, record) =>
        record.file ? (
          <Space style={{ flexDirection: 'column' }}>
            <Button
              type="link"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(record)}
              disabled={downloadingFileId === record.id}
            >
              {downloadingFileId === record.id ? <Spin size="small" /> : 'Download'}
            </Button>
            <span>{record.file.split('/').pop()}</span>
          </Space>
        ) : (
          <span>No File</span>
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this project?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="link" icon={<DeleteOutlined />} danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="projects-page">
      <h3 className="project-title">Projects Management</h3>
      <Button className="add-button" style={{ marginBottom: 16 }} type="primary" onClick={handleAdd}>
        Add Project
      </Button>
      <Space style={{ marginBottom: 26, display: 'flex', justifyContent: 'flex-start' }}>
        <Input.Search
          placeholder="Search projects"
          onSearch={handleSearch}
          enterButton
          allowClear
          style={{ width: 300 }}
        />
        <Select
          placeholder="Select source language"
          onChange={handleSourceLanguageChange}
          style={{ width: 200 }}
          allowClear
        >
          {languages.map((lang) => (
            <Select.Option key={lang.id} value={lang.id}>
              {lang.name} ({lang.code})
            </Select.Option>
          ))}
        </Select>
        <Select
          mode="multiple"
          placeholder="Select target languages"
          onChange={handleTargetLanguagesChange}
          style={{ width: 200 }}
          allowClear
        >
          {languages.map((lang) => (
            <Select.Option key={lang.id} value={lang.id}>
              {lang.name} ({lang.code})
            </Select.Option>
          ))}
        </Select>
        <Select
          placeholder="Select service"
          onChange={handleServiceChange}
          style={{ width: 200 }}
          allowClear
        >
          {services.map((service) => (
            <Select.Option key={service.id} value={service.id}>
              {service.name}
            </Select.Option>
          ))}
        </Select>
      </Space>

      <Table
        dataSource={projects}
        columns={columns}
        loading={projectsLoading}
        rowKey="id"
        scroll={{ x: true }}
        pagination={{
          current: pagination?.current_page || filters.page,
          pageSize: pagination?.per_page || filters.pageSize,
          total: pagination?.total || 0,
          showSizeChanger: false,
        }}
        onChange={handleTableChange}
      />

      {bidModalProject && (
        <BidModal
          project={bidModalProject}
          visible={!!bidModalProject}
          onClose={() => setBidModalProject(null)}
        />
      )}

      <Modal
        title={editingProject ? 'Edit Project' : 'Add Project'}
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={handleSubmit}
        width={800}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description" rules={[{ required: true }]}>
            <Input.TextArea />
          </Form.Item>
          <Form.Item name="source_language_id" label="Source Language" rules={[{ required: true }]}>
            <Select placeholder="Select source language" loading={languagesLoading}>
              {languages.map((lang) => (
                <Select.Option key={lang.id} value={lang.id}>
                  {lang.name} ({lang.code})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="user_id" label="Client" rules={[{ required: true }]}>
            <Select
              placeholder="Select client"
              loading={usersLoading}
              labelInValue // Enable labelInValue
              optionFilterProp="label" // Allow filtering by the label (name)
            >
              {users.map((user) => (
                <Select.Option key={user.id} value={user.id} label={user.name}>
                  {user.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="target_languages" label="Target Languages" rules={[{ required: true }]}>
            <Select mode="multiple" placeholder="Select target languages" loading={languagesLoading}>
              {languages.map((lang) => (
                <Select.Option key={lang.id} value={lang.id}>
                  {lang.name} ({lang.code})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="services" label="Services" rules={[{ required: true }]}>
            <Select mode="multiple" placeholder="Select services" loading={servicesLoading}>
              {services.map((service) => (
                <Select.Option key={service.id} value={service.id}>
                  {service.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="expected_date" label="Expected Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <div style={{ marginBottom: 16 }}>
            <label>Upload File</label>
            <input
              type="file"
              ref={fileInputRef}
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  setSelectedFile(file);
                } else {
                  setSelectedFile(null);
                }
              }}
            />
          </div>
        </Form>
      </Modal>

      {assignModalProjectMemoized && (
        <AssignTranslatorsModal
          project={assignModalProjectMemoized}
          visible={!!assignModalProjectMemoized}
          onClose={() => setAssignModalProject(null)}
        />
      )}
    </div>
  );
};

export default Projects;